import { useEffect, useState } from 'react'

import api from 'repositories/api'
import { IStore } from 'store/modules/store/types'

import { buildSubmissionPlan } from '../domain/submissionPlan'
import { buildGoalPayload, buildSubmittedGoalConfig, createStoreGoal } from '../service'
import { BulkGoalCreationResult, BulkStore, DailyGoalMode, DailyGoalValues, StoreGoalConfig } from '../types'

const MAX_CONCURRENT_SUBMISSIONS = 5
const CREATION_IN_PROGRESS_WARNING = 'A criação das metas ainda está em andamento. Fechar esta guia interromperá o processo e algumas metas podem não ser criadas.'

const mapWithConcurrency = async <T, R>(
  items: T[],
  limit: number,
  mapper: (item: T) => Promise<R>
): Promise<R[]> => {
  const results: R[] = new Array(items.length)
  let cursor = 0
  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (true) {
      const index = cursor++
      if (index >= items.length) return
      results[index] = await mapper(items[index])
    }
  })
  await Promise.all(workers)
  return results
}

interface UseBulkGoalSubmissionProps {
  allStoreCommissionFilled: boolean
  allStoreGoalsFilled: boolean
  allStoreValuesFilled: boolean
  dailyGoalMode: DailyGoalMode
  dailyGoalValues: DailyGoalValues
  selectedAvailableStores: BulkStore[]
  selectedMonth: string
  selectedStoreIds: Set<string>
  storeGoalConfigsByStoreId: Map<string, StoreGoalConfig>
}

export const useBulkGoalSubmission = ({
  allStoreCommissionFilled,
  allStoreGoalsFilled,
  allStoreValuesFilled,
  dailyGoalMode,
  dailyGoalValues,
  selectedAvailableStores,
  selectedMonth,
  selectedStoreIds,
  storeGoalConfigsByStoreId
}: UseBulkGoalSubmissionProps) => {
  const [isCreatingGoals, setIsCreatingGoals] = useState(false)
  const [isCreationDialogOpen, setIsCreationDialogOpen] = useState(false)
  const [creationResults, setCreationResults] = useState<BulkGoalCreationResult[]>([])

  useEffect(() => {
    if (!isCreatingGoals) return undefined

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = CREATION_IN_PROGRESS_WARNING
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [isCreatingGoals])

  const handleCreateBulkGoals = async () => {
    if (!selectedMonth || !dailyGoalMode || !allStoreCommissionFilled || !allStoreValuesFilled || !allStoreGoalsFilled) return
    try {
      setCreationResults([])
      setIsCreationDialogOpen(true)
      setIsCreatingGoals(true)

      const { data: storesAvailability } = await api.axios.get<BulkStore[]>('/store/bulk-goal-options', {
        params: { month: selectedMonth }
      })
      const selectedStoresAvailability = storesAvailability.filter(store => selectedStoreIds.has(store._id))
      const selectedStoresAvailabilityById = new Map(selectedStoresAvailability.map(store => [store._id, store]))
      const submissionPlan = buildSubmissionPlan({
        selectedStoreIds,
        selectedStores: selectedAvailableStores,
        storesAvailabilityById: selectedStoresAvailabilityById
      })

      const storeDetails = submissionPlan.storesToSubmit.length > 0
        ? (await api.axios.post<IStore[]>('/store/bulk-goal-details', {
            storeIds: submissionPlan.storesToSubmit.map(store => store._id)
          })).data
        : []

      const storesById = new Map(storeDetails.map(store => [store._id, store]))
      const createdGoalsResults = await mapWithConcurrency(submissionPlan.storesToSubmit, MAX_CONCURRENT_SUBMISSIONS, async store => {
        try {
          const fullStore = storesById.get(store._id)
          const storeConfig = storeGoalConfigsByStoreId.get(store._id)
          if (!fullStore) throw new Error('Não foi possível carregar os detalhes completos da loja.')
          if (!storeConfig) throw new Error('Não foi possível encontrar a configuração da loja.')
          const submittedConfig = buildSubmittedGoalConfig(storeConfig, { month: selectedMonth, store: fullStore })
          const payload = buildGoalPayload({
            config: submittedConfig,
            dailyGoalMode,
            dailyGoalValues,
            month: selectedMonth,
            storeId: fullStore._id
          })

          const finalGoal = await createStoreGoal(payload)
          return { goalId: finalGoal._id, storeId: store._id, storeName: store.name, success: true }
        } catch (error: unknown) {
          return buildStoreErrorResult(error, store)
        }
      })

      setCreationResults([...submissionPlan.unavailableResults, ...createdGoalsResults])
    } catch (error: unknown) {
      setCreationResults([buildBulkErrorResult(error)])
    } finally {
      setIsCreatingGoals(false)
    }
  }

  return {
    creationResults,
    handleCreateBulkGoals,
    isCreatingGoals,
    isCreationDialogOpen,
    setCreationResults,
    setIsCreationDialogOpen
  }
}

const getFriendlyError = (error: unknown) => {
  const requestError = error as { code?: string; message?: string; response?: { data?: { error?: { friendlyMsg?: string } } } }
  if (requestError.code === 'ECONNABORTED' || requestError.message?.toLowerCase().includes('timeout')) {
    return 'Não foi possível confirmar a criação dentro do tempo esperado.'
  }
  return requestError.response?.data?.error?.friendlyMsg || requestError.message
}

const buildStoreErrorResult = (error: unknown, store: BulkStore) => ({
  errorMessage: getFriendlyError(error) || 'Não foi possível criar a meta desta loja.',
  storeId: store._id,
  storeName: store.name,
  success: false
})

const buildBulkErrorResult = (error: unknown) => ({
  errorMessage: getFriendlyError(error) || 'Não foi possível concluir a criação das metas.',
  storeId: 'bulk-create-error',
  storeName: 'Criação das metas',
  success: false
})
